import { createColumnHelper, type SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RowValue } from '@/components/data/DataTable'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { TableEmptyState } from '@/components/data/TableEmptyState'
import { CountryText } from '@/components/ui/CountryText'
import { StatusPill } from '@/components/ui/StatusPill'
import { formatNumber } from '@/lib/format'
import type { CounterpartiesMoney, CounterpartyMoneyItem, CounterpartyRow, Money } from '@/types/domain'

/* The counterparty list on the shared dense-table layer (S-22b): Контрагент ·
 * Категория · Страна, and since S-22e Перевозки · Начислено · Оплачено ·
 * Остаток к оплате from the list money, merged by id. The category is plain
 * text, not a coloured tag (Locked visual decision), and its column sorts by
 * the words the reader sees, not by the code. Below the table breakpoint a row
 * becomes a card (RecordList), so a 390px phone never scrolls sideways.
 *
 * The money columns are NOT sortable (owner: the list keeps its sorting), and
 * they exist only when figures were served — not while they load, not for a
 * reader whose money is forbidden, not when the read failed (the page says so
 * above the table). A counterparty with no item has no work in the reader's
 * reach and reads «—», never a zero nobody served. */

const column = createColumnHelper<CounterpartyRow>()

/** A served figure, grouped; `empty` («—») for a counterparty with no item. */
function moneyCell(value: Money | undefined, empty: string, emphasis = false) {
  if (!value) return empty
  return (
    <span className={emphasis ? 'font-semibold' : undefined}>{formatNumber(value.amount, 2)}</span>
  )
}

export function CounterpartiesTable({
  rows,
  filtered,
  onClearFilters,
  onOpen,
  money,
}: {
  rows: CounterpartyRow[]
  /** The list money; its columns show only when it was served. */
  money?: CounterpartiesMoney
  /** True when a chip, the search or the box emptied the table — which the
   *  table cannot tell from "no counterparties yet". */
  filtered: boolean
  onClearFilters: () => void
  onOpen: (row: CounterpartyRow) => void
}) {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState<SortingState>([])
  const served = money !== undefined && money.moneyUnavailable === undefined
  const byId = useMemo(
    () =>
      new Map<string, CounterpartyMoneyItem>(
        (served ? money.items : []).map((item) => [item.counterpartyId, item]),
      ),
    [served, money],
  )

  const columns = useMemo(
    () => [
      column.accessor('name', {
        header: t('counterparties.columns.counterparty'),
        cell: (info) => (
          <RowValue
            primary={info.getValue()}
            // Said in words beside the name: the «Неактивные» box is the only
            // way such a row is listed, and a reader who forgot it was ticked
            // must still see why this one is different.
            secondary={info.row.original.isActive ? undefined : t('common.form.inactive')}
          />
        ),
      }),
      column.accessor((row) => t(`counterpartyCategory.${row.category}`), {
        id: 'category',
        header: t('counterparties.columns.category'),
        size: 200,
      }),
      column.accessor('country', {
        header: t('counterparties.columns.country'),
        size: 200,
        cell: (info) => <CountryText value={info.getValue()} empty={t('common.empty')} />,
      }),
      ...(served
        ? [
            column.accessor((row) => byId.get(row.id)?.units, {
              id: 'units',
              header: t('counterparties.money.columns.units'),
              size: 110,
              enableSorting: false,
              meta: { numeric: true },
              cell: (info) => {
                const units = info.getValue()
                return units === undefined ? t('common.empty') : formatNumber(units)
              },
            }),
            column.accessor((row) => byId.get(row.id)?.billed, {
              id: 'billed',
              header: t('counterparties.money.columns.billed'),
              size: 140,
              enableSorting: false,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue(), t('common.empty')),
            }),
            column.accessor((row) => byId.get(row.id)?.paid, {
              id: 'paid',
              header: t('counterparties.money.columns.paid'),
              size: 140,
              enableSorting: false,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue(), t('common.empty')),
            }),
            column.accessor((row) => byId.get(row.id)?.balance, {
              id: 'balance',
              header: t('counterparties.money.columns.balance'),
              size: 160,
              enableSorting: false,
              meta: { numeric: true },
              cell: (info) => moneyCell(info.getValue(), t('common.empty'), true),
            }),
          ]
        : []),
    ],
    [t, served, byId],
  )

  return (
    <RecordList
      label={t('counterparties.table.label')}
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      onRowClick={onOpen}
      sorting={sorting}
      onSortingChange={setSorting}
      filtered={filtered}
      onClearFilters={onClearFilters}
      empty={
        <TableEmptyState
          filtered={false}
          title={t('counterparties.empty.title')}
          description={t('counterparties.empty.description')}
        />
      }
      renderCard={(row) => (
        <RecordCard
          id={row.name}
          status={
            row.isActive ? undefined : (
              <StatusPill tone="neutral">{t('common.form.inactive')}</StatusPill>
            )
          }
          primary={t(`counterpartyCategory.${row.category}`)}
          secondary={row.country.trim() ? <CountryText value={row.country} /> : undefined}
          value={served ? moneyCell(byId.get(row.id)?.balance, t('common.empty')) : undefined}
          valueLabel={served ? t('counterparties.money.columns.balance') : undefined}
        />
      )}
    />
  )
}
